/**
 * THE node-type colour palette. One module, one array — every surface that
 * paints a node type (graph canvas, chips, legends, cover PNGs, agent tool
 * args) resolves through here.
 *
 * History: the palette forked in March 2026. `apps/web/src/utils/colors.ts`
 * (later `packages/ui/src/utils/colors.ts`) had two entries replaced for
 * dark-mode contrast in 569eb60c1 — `#264653` -> `#4BA3B7` and `#073B4C` ->
 * `#158DA0` — while the copy here (named in ccfbdc128) kept the pre-fix hexes.
 * The VISIBLE palette wins: the array below is the post-contrast-fix one users
 * have been seeing in their graphs (and the one the backend cover renderer was
 * already byte-matching). The two retired hexes still round-trip through
 * `getColorName` via `LEGACY_COLOR_HEXES`, so schema colours persisted from the
 * old names keep resolving to a human-readable label.
 */

/** Curated palette of 30 vibrant, distinct colours optimized for differentiation. */
export const COLOR_PALETTE_NAMED: ReadonlyArray<{ name: string; hex: string }> = [
	{ name: 'Crimson Red', hex: '#E63946' },
	{ name: 'Orange', hex: '#F77F00' },
	{ name: 'Saffron Yellow', hex: '#FCBF49' },
	{ name: 'Cream', hex: '#EAE2B7' },
	{ name: 'Teal', hex: '#2A9D8F' },
	{ name: 'Teal Slate', hex: '#4BA3B7' },
	{ name: 'Burnt Sienna', hex: '#E76F51' },
	{ name: 'Sandy Brown', hex: '#F4A261' },
	{ name: 'Violet', hex: '#8338EC' },
	{ name: 'Bright Blue', hex: '#3A86FF' },
	{ name: 'Hot Pink', hex: '#FF006E' },
	{ name: 'Vivid Orange', hex: '#FB5607' },
	{ name: 'Amber', hex: '#FFBE0B' },
	{ name: 'Emerald', hex: '#06D6A0' },
	{ name: 'Cerulean', hex: '#118AB2' },
	{ name: 'Cyan', hex: '#158DA0' },
	{ name: 'Lavender Purple', hex: '#9B5DE5' },
	{ name: 'Sky Blue', hex: '#00BBF9' },
	{ name: 'Aquamarine', hex: '#00F5D4' },
	{ name: 'Bright Yellow', hex: '#FEE440' },
	{ name: 'Deep Purple', hex: '#7209B7' },
	{ name: 'Indigo', hex: '#560BAD' },
	{ name: 'Blue Violet', hex: '#480CA8' },
	{ name: 'Royal Blue', hex: '#3F37C9' },
	{ name: 'Ultramarine', hex: '#4361EE' },
	{ name: 'Light Blue', hex: '#4CC9F0' },
	{ name: 'Magenta', hex: '#F72585' },
	{ name: 'Fuchsia', hex: '#B5179E' },
	{ name: 'Grape Purple', hex: '#7B2CBF' },
	{ name: 'Terra Cotta', hex: '#E07A5F' },
];

/**
 * Hexes retired by the March 2026 contrast fix. They are NOT offered to the
 * agent (`COLOR_NAMES` / `parseColorName` only surface live palette entries)
 * but they are still persisted on schemas created before the fix, so
 * `getColorName` must keep naming them.
 */
const LEGACY_COLOR_HEXES: ReadonlyArray<{ name: string; hex: string; replacedBy: string }> = [
	{ name: 'Dark Slate', hex: '#264653', replacedBy: '#4BA3B7' },
	{ name: 'Dark Cyan', hex: '#073B4C', replacedBy: '#158DA0' },
];

export const COLOR_PALETTE: string[] = COLOR_PALETTE_NAMED.map((c) => c.hex);

export const COLOR_NAMES: string[] = COLOR_PALETTE_NAMED.map((c) => c.name);

const NAME_TO_HEX = new Map<string, string>([
	...COLOR_PALETTE_NAMED.map((c) => [c.name.toLowerCase(), c.hex] as const),
	// A legacy name resolves FORWARD to its replacement, so an agent echoing a
	// name it read off an old schema still gets a readable colour.
	...LEGACY_COLOR_HEXES.map((c) => [c.name.toLowerCase(), c.replacedBy] as const),
]);
const HEX_TO_NAME = new Map<string, string>([
	...COLOR_PALETTE_NAMED.map((c) => [c.hex.toUpperCase(), c.name] as const),
	...LEGACY_COLOR_HEXES.map((c) => [c.hex.toUpperCase(), c.name] as const),
]);

// Resolve an agent-facing color name to the canonical hex value.
// Returns null if the name is not in the palette — callers surface this as
// a validation error listing the allowed names, so the agent can retry.
export const parseColorName = (name: string | undefined | null): string | null => {
	if (!name || typeof name !== 'string') return null;
	return NAME_TO_HEX.get(name.trim().toLowerCase()) ?? null;
};

export const getColorName = (hex: string | undefined | null): string | null => {
	if (!hex || typeof hex !== 'string') return null;
	return HEX_TO_NAME.get(hex.trim().toUpperCase()) ?? null;
};

/** System types that always paint the same colour, whatever the schema says. */
const SYSTEM_TYPE_COLORS: Readonly<Record<string, string>> = {
	Thread: '#6366f1', // Indigo
	Message: '#a855f7', // Purple
	Note: '#d4d4d8', // Neutral zinc-300
};

/**
 * Per-type colour overrides, as read off a space's schema (`node.color`).
 * Accepted as either a Map or a plain object so callers can hand over whatever
 * shape they already have (schema parsers build Maps; graph exports ship JSON).
 */
export type ColorOverrides = ReadonlyMap<string, string> | Readonly<Record<string, string>>;

const readOverride = (overrides: ColorOverrides | undefined, type: string): string | undefined => {
	if (!overrides) return undefined;
	if (overrides instanceof Map) return overrides.get(type);
	return (overrides as Record<string, string>)[type];
};

// Deterministic-hash results are override-independent, so this cache is safe to
// share across resolvers: it only ever holds the palette fallback for a type.
const paletteCache: Map<string, string> = new Map();

const paletteColorForType = (type: string): string => {
	const cached = paletteCache.get(type);
	if (cached) return cached;

	// Hash the type string to get a consistent number
	let hash = 0;
	for (let i = 0; i < type.length; i++) {
		const char = type.charCodeAt(i);
		hash = (hash << 5) - hash + char;
		hash = hash & hash; // Convert to 32-bit integer
	}

	const color = COLOR_PALETTE[Math.abs(hash) % COLOR_PALETTE.length];
	paletteCache.set(type, color);
	return color;
};

/**
 * The colour for a node type: a system colour, else the schema override, else a
 * deterministic pick from the palette.
 *
 * `overrides` is EXPLICIT on purpose — this module holds no mutable global, so
 * two graphs rendered on one page cannot bleed colours into each other. Pass
 * the schema's `type -> color` map (see `createColorResolver` for the curried
 * form the canvas painters take).
 */
export const getColorForType = (
	type: string | undefined | null,
	overrides?: ColorOverrides,
): string => {
	// Guard against non-string input (graph data may have unexpected types)
	const key = typeof type === 'string' ? type : String(type ?? '');

	const system = SYSTEM_TYPE_COLORS[key];
	if (system) return system;

	const override = readOverride(overrides, key);
	if (override) return override;

	return paletteColorForType(key);
};

/** A `type -> hex` function with a fixed set of overrides baked in. */
export type ColorResolver = (type: string) => string;

/**
 * Bind a set of schema overrides once and hand the resulting resolver to
 * whatever needs to paint — canvas draw functions, tooltips, legends. Stable
 * for a stable `overrides` reference, so it is safe as a `useMemo` value.
 */
export const createColorResolver = (overrides?: ColorOverrides): ColorResolver => {
	if (!overrides) return (type: string) => getColorForType(type);
	return (type: string) => getColorForType(type, overrides);
};
